import * as H from "history";
import * as React from "react";
import {
  RouteChildrenProps,
  RouteProps
} from "react-router-dom";

import { Icons } from "src/library/components/Icon/types";
import type { InstalledModuleType } from "src/utils/constants";
import type { MenuType } from "src/utils/menu/menus";
import type { ERPType } from "src/core/shared/api/globalSettings.api";
import type { ContextType } from "src/core/shared/hooks/menu/useMenu";
// admin routes
import Main from "src/pages/";
import AdminCustomersAndUsers from "src/pages/Admin/CustomersAndUsers";
import AdminCustomer from "src/pages/Admin/CustomersAndUsers/Customer";
import AdminCustomerDrops from "src/pages/Admin/CustomersAndUsers/Customer/Drops";
import AdminCustomerUsers from "src/pages/Admin/CustomersAndUsers/Customer/Users";
import AdminUsers from "src/pages/Admin/CustomersAndUsers/Users";
import DeliveryRoutes from "src/pages/Admin/DeliveryRoutes";
import AdminNotifications from "src/pages/Admin/Notifications";
import AdminNotificationsNotification from "src/pages/Admin/Notifications/Notification";
import AdminProducts from "src/pages/Admin/Products";
import AdminPromotions from "src/pages/Admin/Promotions";
import AdminProductsProduct from "src/pages/Admin/Products/Product";
import AdminSettings from "src/pages/Admin/Settings";
import CustomerLists from "src/pages/Customer/Lists";
import AdminSupport from "src/pages/Admin/Support";
// Customer routes
import CustomerOrdersRoutes from "src/pages/Customer/Orders";
import CustomerProductsRoutes from "src/pages/Customer/Products";
import CustomerSettings from "src/pages/Customer/Settings";
// Public routes
import { PublicRoutes } from "src/pages/Public";
import type { IRouteProps } from "src/pages/Router/Route";
import type {
  PermissionCodeType,
  RoleType
} from "src/core/shared/api/roles.api";
import { IStore } from "src/core/shared/store/index";
import { canViewByRoleCodes } from "src/core/shared/utils/canViewByRoleCodes";

import PaymentMethodRoutes from "../Customer/PaymentMethods";
import CustomerSupport from "../Customer/Support";
import HelpSection from "../Customer/Help";
import DropShutdowns from "../Customer/DropShutdowns";
import type { OrdersViewType } from "../Customer/Orders/OrdersPage";
import AdminOrders from "../Admin/Orders";
import VanSalesOrdersPage from "../Customer/Orders/VanSalesOrdersPage";
import LeadTimeRoutes from "../Admin/Settings/LeadTimes";
import CustomerWasteManagement from "../Customer/WasteManagement";
import CustomerAccounting from "../Customer/Accounting";
import AdminAccounting from "../Admin/Accounting";

export type PageProps<T, U extends { [K in keyof U]?: string | undefined; } = { [K: string]: string | undefined; }> = RouteChildrenProps<U, ILocationState> & IRouteProps & T;

export interface ILocationState {
  notification?: {
    type: "success" | "info" | "warn" | "error";
    message: string;
  };
  edit?: boolean;
  ordersViewType?: OrdersViewType;
  from?: string;
  wasteDate?: string;
}

export interface IRoute extends Omit<RouteProps, "children"> {
  location?: H.Location<ILocationState>;
  path?: string;
  loadedPath?: string;
  pageComponent?: React.ReactFCWithChildren<any>;
  title: string | null;
  menuType: MenuType;
  privateRoute?: boolean;
  allowedContexts?: ContextType[];
  context?: ContextType;
  contextMenu?: { [key in ContextType]?: Partial<IRoute> };
  redirect?: string;
  permissions?: PermissionCodeType[];
  roles?: RoleType[];
  erp?: ERPType[];
  modules?: InstalledModuleType[];
  menuTitle?: string;
  icon?: Icons;
  parent?: IRoute;
  disabled?: boolean;
  hidden?: boolean;
  onClick?: () => void;
  canView?: (store: IStore) => boolean;
  children?: React.ReactNode;
}

export const Routes: IRoute[] = [
  // public routes
  ...PublicRoutes,

  // MAIN MENU
  ...Main,

  // Admin routes
  ...AdminSettings,
  ...AdminCustomersAndUsers,
  ...AdminCustomer,
  ...AdminCustomerUsers,
  ...AdminCustomerDrops,
  ...AdminUsers,
  ...AdminNotifications,
  ...AdminNotificationsNotification,
  ...AdminProducts,
  ...AdminPromotions,
  ...AdminOrders,
  ...AdminProductsProduct,
  ...DeliveryRoutes,
  ...LeadTimeRoutes,
  ...AdminSupport,
  ...AdminAccounting,
  {
    exact: true,
    path: "/van-sales",
    title: "Dashboard",
    pageComponent: VanSalesOrdersPage,
    icon: Icons.VAN,
    menuType: "ADMIN_VAN_SALES",
    privateRoute: true,
    permissions: [ "view-van-sales-ordering" ],
    roles: [ "admin" ]
  },
  {
    path: "/platform/defaults",
    canView: store => canViewByRoleCodes(store, [ "account-manager" ], "DENY"),
    title: "Platform Settings",
    privateRoute: true,
    menuType: "ADMIN_MAIN",
    icon: Icons.SETTINGS,
    permissions: [ "view-global-settings" ],
    roles: [ "admin" ]
  },

  // customer routes
  ...CustomerLists,
  ...CustomerOrdersRoutes,
  ...CustomerProductsRoutes,
  ...CustomerSettings,
  ...PaymentMethodRoutes,
  ...CustomerWasteManagement,
  ...HelpSection,
  ...DropShutdowns,
  ...CustomerSupport,
  ...CustomerAccounting
];
